import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const InsightSummaryPanel = ({ insights = [], recommendations = [] }) => {
  const [activeTab, setActiveTab] = useState('insights');

  const mockInsights = [
    { id: 1, type: 'trend', title: 'Crescimento Acelerado', description: `A receita apresentou crescimento de 48% nos últimos 3 meses, superando as projeções em 12%. O crescimento é impulsionado principalmente pelo aumento de 35% no ticket médio e 15% na base de clientes.`, confidence: 95, impact: 'high', category: 'revenue', timestamp: '2025-08-29T10:00:00Z' },
    { id: 2, type: 'anomaly', title: 'Eficiência Operacional', description: `Detectada melhoria significativa na eficiência operacional (89% vs 78% no período anterior). Redução de 15% nos custos operacionais mantendo qualidade do serviço.`, confidence: 87, impact: 'medium', category: 'operations', timestamp: '2025-08-29T09:45:00Z' },
    { id: 3, type: 'correlation', title: 'Correlação Cliente-Receita', description: `Identificada forte correlação (r=0.92) entre satisfação do cliente e crescimento da receita. Clientes com NPS > 8 geram 40% mais receita por transação.`, confidence: 91, impact: 'high', category: 'customer', timestamp: '2025-08-29T09:30:00Z' },
    { id: 4, type: 'prediction', title: 'Projeção Q4', description: `Modelo preditivo indica crescimento de 25-30% no Q4, baseado em sazonalidade histórica e tendências atuais. Margem de erro: ±5%.`, confidence: 83, impact: 'high', category: 'forecast', timestamp: '2025-08-29T09:15:00Z' }
  ];

  const mockRecommendations = [
    { id: 1, priority: 'high',   title: 'Expandir Capacidade Produtiva', description: `Com base no crescimento projetado de 30% para Q4, recomenda-se aumentar a capacidade produtiva em 25% até outubro para atender a demanda sem comprometer a qualidade.`, expectedImpact: 'Aumento de 15-20% na receita', timeframe: '60 dias', effort: 'high',   category: 'operations' },
    { id: 2, priority: 'medium', title: 'Programa de Fidelização',       description: `Implementar programa de fidelização focado em clientes com NPS > 8, que demonstraram maior potencial de receita. Incluir benefícios exclusivos e comunicação personalizada.`, expectedImpact: 'Aumento de 10-15% na retenção', timeframe: '30 dias', effort: 'medium', category: 'customer' },
    { id: 3, priority: 'medium', title: 'Otimização de Custos',          description: `Aproveitar a melhoria na eficiência operacional para renegociar contratos com fornecedores e otimizar processos, mantendo a qualidade atual.`, expectedImpact: 'Redução de 8-12% nos custos', timeframe: '45 dias', effort: 'low',    category: 'finance' },
    { id: 4, priority: 'low',    title: 'Análise de Mercado',            description: `Realizar estudo detalhado do market share para identificar oportunidades de expansão em segmentos adjacentes com base no crescimento atual.`, expectedImpact: 'Identificação de 2-3 novos segmentos', timeframe: '90 dias', effort: 'medium', category: 'strategy' }
  ];

  const currentInsights = insights?.length > 0 ? insights : mockInsights;
  const currentRecommendations = recommendations?.length > 0 ? recommendations : mockRecommendations;

  const getInsightIcon = (t) => (t === 'trend' ? 'TrendingUp' : t === 'anomaly' ? 'AlertCircle' : t === 'correlation' ? 'GitBranch' : t === 'prediction' ? 'Target' : 'Info');
  const getImpactColor = (i) => (i === 'high' ? 'text-success' : i === 'medium' ? 'text-warning' : 'text-muted-foreground');
  const getPriorityColor = (p) => (p === 'high' ? 'bg-error/10 text-error border-error/20' : p === 'medium' ? 'bg-warning/10 text-warning border-warning/20' : 'bg-muted text-muted-foreground border-border');
  const getEffortIcon = (e) => (e === 'high' ? 'Zap' : e === 'medium' ? 'Clock' : e === 'low' ? 'Feather' : 'Circle');

  return (
    <div className="bg-card rounded-lg border border-border card-shadow overflow-hidden">
      <div className="p-6 border-b border-border">
        <h3 className="font-heading-semibold text-lg text-card-foreground mb-4">
          Inteligência de Negócios
        </h3>

        {/* Abas com scroll horizontal (evita vazar) */}
        <div className="flex gap-2 bg-muted rounded-lg p-1 overflow-x-auto no-scrollbar">
          <button
            onClick={() => setActiveTab('insights')}
            className={`px-3 py-2 rounded-md text-sm font-body-medium transition-smooth
              whitespace-nowrap overflow-hidden shrink-0
              ${activeTab === 'insights'
                ? 'bg-card text-card-foreground shadow-sm'
                : 'text-muted-foreground hover:text-card-foreground'}
            `}
          >
            <span className="inline-flex items-center gap-2">
              <Icon name="Lightbulb" size={16} />
              <span>Insights</span>
              <span className="px-1.5 py-0.5 bg-primary/20 text-primary text-xs rounded-full">
                {currentInsights?.length}
              </span>
            </span>
          </button>

          <button
            onClick={() => setActiveTab('recommendations')}
            className={`px-3 py-2 rounded-md text-sm font-body-medium transition-smooth
              whitespace-nowrap overflow-hidden shrink-0
              ${activeTab === 'recommendations'
                ? 'bg-card text-card-foreground shadow-sm'
                : 'text-muted-foreground hover:text-card-foreground'}
            `}
          >
            <span className="inline-flex items-center gap-2">
              <Icon name="Target" size={16} />
              <span>Recomendações</span>
              <span className="px-1.5 py-0.5 bg-accent/20 text-accent-foreground text-xs rounded-full">
                {currentRecommendations?.length}
              </span>
            </span>
          </button>
        </div>
      </div>

      <div className="p-6 max-h-96 overflow-y-auto">
        {activeTab === 'insights' ? (
          <div className="space-y-4">
            {currentInsights?.map((insight) => (
              <div key={insight?.id} className="p-4 bg-muted/50 rounded-lg border border-border/50">
                <div className="flex items-start gap-3">
                  <div className="mt-1">
                    <Icon name={getInsightIcon(insight?.type)} size={18} className="text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-2 gap-3">
                      <h4 className="font-body-medium text-sm text-card-foreground">{insight?.title}</h4>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className={`text-xs font-body-medium ${getImpactColor(insight?.impact)}`}>
                          {insight?.impact === 'high' ? 'Alto Impacto' : insight?.impact === 'medium' ? 'Médio Impacto' : 'Baixo Impacto'}
                        </span>
                        <div className="flex items-center gap-1">
                          <Icon name="Zap" size={12} className="text-muted-foreground" />
                          <span className="text-xs text-muted-foreground">{insight?.confidence}%</span>
                        </div>
                      </div>
                    </div>

                    <p className="text-xs text-muted-foreground leading-relaxed mb-2">
                      {insight?.description}
                    </p>

                    <div className="flex items-center justify-between">
                      <span className="text-xs text-muted-foreground font-caption">
                        {new Date(insight.timestamp)?.toLocaleString('pt-BR')}
                      </span>
                      <Button variant="ghost" size="sm">Ver Detalhes</Button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-4">
            {currentRecommendations?.map((rec) => (
              <div key={rec?.id} className="p-4 bg-muted/50 rounded-lg border border-border/50">
                <div className="flex items-start gap-3">
                  <div className="mt-1">
                    <Icon name="Target" size={18} className="text-accent" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-2 gap-3">
                      <h4 className="font-body-medium text-sm text-card-foreground">{rec?.title}</h4>
                      <span className={`px-2 py-1 rounded-full text-xs font-body-medium border ${getPriorityColor(rec?.priority)} shrink-0`}>
                        {rec?.priority === 'high' ? 'Alta' : rec?.priority === 'medium' ? 'Média' : 'Baixa'}
                      </span>
                    </div>

                    <p className="text-xs text-muted-foreground leading-relaxed mb-3">
                      {rec?.description}
                    </p>

                    <div className="grid grid-cols-2 gap-3 text-xs">
                      <div>
                        <span className="text-muted-foreground">Impacto Esperado:</span>
                        <p className="font-body-medium text-card-foreground">{rec?.expectedImpact}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Prazo:</span>
                        <p className="font-body-medium text-card-foreground">{rec?.timeframe}</p>
                      </div>
                    </div>

                    <div className="flex items-center justify-between mt-3 pt-2 border-t border-border/50">
                      <div className="flex items-center gap-2">
                        <Icon name={getEffortIcon(rec?.effort)} size={12} className="text-muted-foreground" />
                        <span className="text-xs text-muted-foreground">
                          Esforço: {rec?.effort === 'high' ? 'Alto' : rec?.effort === 'medium' ? 'Médio' : 'Baixo'}
                        </span>
                      </div>
                      <Button variant="outline" size="sm">Implementar</Button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="p-4 border-t border-border bg-muted/30">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Icon name="Brain" size={14} />
            <span>IA atualizada em: 29/08/2025 10:05</span>
          </div>
          <Button variant="ghost" size="sm" iconName="RefreshCw">Atualizar</Button>
        </div>
      </div>
    </div>
  );
};

export default InsightSummaryPanel;
